import PrivateCoinExpiry from '../components/PrivateCoinExpiry';
import { coinValidityDate } from '../utils/coinValidity';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { observer } from 'mobx-react-lite';
import moment from 'moment';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown, faChevronLeft, faChevronUp, faStar } from '@fortawesome/free-solid-svg-icons';
import { useNavigate } from 'react-router-dom';
import { RootStoreContext } from '../stores/RootStore';
import {
  cancelMembership,
  changeMembership,
  createMembershipCheckout,
  createMembershipCardSetup,
  completeMembershipCardSetup,
  createMembershipTopupCheckout,
  createTrainingPackageCheckout,
  getPackagesCatalog,
  getPackagesStatus,
  getStudentProgress,
  keepMembershipActive,
  AttendanceStats,
  ActivePackageStatus,
  PackageCatalogItem,
  PackagesCatalogResponse,
  PackagesStatusResponse
} from '../api/API';
import { TrainingPackage, User } from '../config/Registration';
import './PackagesMembership.css';

export type ActiveTab = 'training' | 'membership' | 'private';

const RECENT_WEEKS = 8;
const RECOMMENDATION_COUNT = 3;
// Recent weekly attendance at/above this maps to the most expensive tier band.
const WEEKLY_RATE_CAP = 4;
const YOUTH_MAX_AGE = 26;
const PRIVATE_CLASS_RESUME_STORAGE_KEY = 'private-classes-booking-resume';

const hasPrivateClassResume = () => {
  try {
    return !!sessionStorage.getItem(PRIVATE_CLASS_RESUME_STORAGE_KEY);
  } catch {
    return false;
  }
};

// ISO-week key matching the backend's attendedWeeks map (e.g. "2026-W27").
const getISOWeekString = (date: Date): string => {
  const d = new Date(date.getTime());
  d.setDate(d.getDate() + 3 - ((d.getDay() + 6) % 7));
  const yearStart = new Date(d.getFullYear(), 0, 4);
  yearStart.setDate(yearStart.getDate() + 3 - ((yearStart.getDay() + 6) % 7));
  const weekNo = 1 + Math.round(((d.getTime() - yearStart.getTime()) / 86400000 - 3 + ((yearStart.getDay() + 6) % 7)) / 7);
  return `${d.getFullYear()}-W${String(weekNo).padStart(2, '0')}`;
};

// Average classes per week over the last RECENT_WEEKS, from AttendanceStats.attendedWeeks.
const recentWeeklyRate = (stats: AttendanceStats | null): number => {
  if (!stats?.attendedWeeks) {
    return 0;
  }
  const now = new Date();
  let total = 0;
  for (let i = 0; i < RECENT_WEEKS; i++) {
    const day = new Date(now.getTime() - i * 7 * 86400000);
    total += stats.attendedWeeks[getISOWeekString(day)] || 0;
  }
  return total / RECENT_WEEKS;
};

// Collapse variations to one representative package per size (package_option_id),
// preferring the "Standard" variation, else the cheapest; sorted ascending by coins.
const buildTiers = (items: PackageCatalogItem[]): PackageCatalogItem[] => {
  const groups = new Map<number, PackageCatalogItem[]>();
  items.forEach(item => {
    const arr = groups.get(item.package_option_id) || [];
    arr.push(item);
    groups.set(item.package_option_id, arr);
  });
  const reps: PackageCatalogItem[] = [];
  groups.forEach(arr => {
    const standard = arr.find(variation => /standard/i.test(variation.variation_name));
    reps.push(standard || [...arr].sort((a, b) => Number(a.price_eur) - Number(b.price_eur))[0]);
  });
  return reps.sort((a, b) => a.coins_amount - b.coins_amount);
};

// The student's current level: recent attendance mapped onto the tier ladder.
const attendanceTierIndex = (count: number, weeklyRate: number): number => {
  if (count <= 1) {
    return 0;
  }
  const fraction = Math.min(1, Math.max(0, weeklyRate / WEEKLY_RATE_CAP));
  return Math.round(fraction * (count - 1));
};

// Prefer the "Standard" variation, then normal ones, then trial/beginner offers last.
const variationRank = (variationName: string): number => {
  if (/standard/i.test(variationName)) return 0;
  if (/trial|beginner/i.test(variationName)) return 2;
  return 1;
};

const isSmallTier = (item: PackageCatalogItem): boolean => item.name.trim().toUpperCase() === 'S';

const isExtenderPackage = (item: PackageCatalogItem): boolean =>
  /\b(?:extend(?:er|ed|ing|s)?|extension)\b/i.test(`${item.name} ${item.variation_name} ${item.display_name}`);

export const packagesForTab = (catalog: PackagesCatalogResponse, tab: ActiveTab): PackageCatalogItem[] => {
  const allItems = [...catalog.training_packages, ...catalog.memberships];
  if (tab === 'private') return allItems.filter(item => item.is_private_class_package);
  if (tab === 'membership') return catalog.memberships.filter(item => !item.is_private_class_package);
  return catalog.training_packages.filter(item => !item.is_private_class_package);
};

export const sortTrainingPackagesByPrice = (items: PackageCatalogItem[]): PackageCatalogItem[] =>
  [...items].sort((a, b) => Number(b.price_eur) - Number(a.price_eur));

export const sortMembershipPlansByPrice = (items: PackageCatalogItem[]): PackageCatalogItem[] =>
  [...items].sort((a, b) => Number(b.price_eur) - Number(a.price_eur));

export const getTrainingPackageDisplay = (
  availableItems: PackageCatalogItem[],
  recommendedItems: PackageCatalogItem[],
  showAll: boolean
) => ({
  showRecommendedSection: !showAll && recommendedItems.length > 0,
  items: showAll ? availableItems : recommendedItems
});

export const getPackageCardTitle = (
  item: PackageCatalogItem,
  membership = item.is_membership
): string => {
  const variationName = item.variation_name?.trim();
  const displayName = item.display_name?.trim();

  if (variationName?.toLowerCase() === 'standard') {
    return membership || item.is_private_class_package ? item.name : `${item.name}-package`;
  }

  if (membership) {
    return displayName || item.name;
  }

  if (displayName && displayName !== item.name) {
    return displayName;
  }

  return variationName ? `${item.name} (${variationName})` : item.name;
};

export const isYouthPackage = (item: PackageCatalogItem): boolean =>
  /youth/i.test(item.display_name || item.name);

export const filterPackagesByYouth = (
  items: PackageCatalogItem[],
  showYouthPackages: boolean
): PackageCatalogItem[] => items.filter(item => isYouthPackage(item) === showYouthPackages);

export const computeUserAge = (
  user?: Pick<User, 'birthYear' | 'birthMonth' | 'birthDay'>,
  now = new Date()
): number | null => {
  if (user?.birthYear == null) {
    return null;
  }

  let age = now.getFullYear() - user.birthYear;
  if (user.birthMonth != null) {
    const birthdayIsLater = now.getMonth() + 1 < user.birthMonth
      || (now.getMonth() + 1 === user.birthMonth
        && user.birthDay != null
        && now.getDate() < user.birthDay);
    if (birthdayIsLater) {
      age -= 1;
    }
  }
  return age;
};

export const shouldShowYouthPackageToggle = (
  user?: Pick<User, 'birthYear' | 'birthMonth' | 'birthDay'>,
  now = new Date()
): boolean => {
  const age = computeUserAge(user, now);
  return age == null || age <= YOUTH_MAX_AGE;
};

// A student's first recommendations must be real coin packages. Start with the
// studio-configured tier (for example "S" or "M"), then offer the next two larger
// tiers. Prefer Standard variations and gracefully fall back to S, then the
// smallest non-extender package.
const firstPackageRecommendation = (
  items: PackageCatalogItem[],
  configuredTierName: string
): PackageCatalogItem[] => {
  const purchasable = items.filter(item => !isExtenderPackage(item) && item.coins_amount > 0);
  if (purchasable.length === 0) {
    return [];
  }

  const normalizedConfiguredName = configuredTierName.trim().toUpperCase();
  const configuredTier = purchasable.filter(item => item.name.trim().toUpperCase() === normalizedConfiguredName);
  const smallTier = purchasable.filter(isSmallTier);
  const candidates = configuredTier.length > 0 ? configuredTier : smallTier.length > 0 ? smallTier : purchasable;
  const recommended = [...candidates].sort((a, b) =>
    variationRank(a.variation_name) - variationRank(b.variation_name)
      || a.coins_amount - b.coins_amount
      || Number(a.price_eur) - Number(b.price_eur)
  )[0];

  if (!recommended) {
    return [];
  }

  const largerTiers = buildTiers(purchasable)
    .filter(item =>
      item.package_option_id !== recommended.package_option_id
      && item.coins_amount > recommended.coins_amount
    )
    .slice(0, RECOMMENDATION_COUNT - 1);

  return [recommended, ...largerTiers];
};

// Recommend 2 packages at the student's current level (same coins amount) + 1 at the
// next level up. Falls back to nearby tiers so we still surface RECOMMENDATION_COUNT,
// and reserves one recommendation for the S package when that tier is available.
export const buildRecommendations = (
  items: PackageCatalogItem[],
  weeklyRate: number,
  hasActivePackage = true,
  defaultPackageName = 'S'
): PackageCatalogItem[] => {
  if (!hasActivePackage) {
    return sortTrainingPackagesByPrice(firstPackageRecommendation(items, defaultPackageName));
  }

  const tiers = buildTiers(items);
  if (tiers.length === 0) {
    return [];
  }
  const currentIdx = attendanceTierIndex(tiers.length, weeklyRate);
  const currentSize = tiers[currentIdx].package_option_id;

  // "2 the same amount": up to 2 variations at the student's current size.
  const recs = items
    .filter(item => item.package_option_id === currentSize)
    .sort((a, b) => variationRank(a.variation_name) - variationRank(b.variation_name) || Number(a.price_eur) - Number(b.price_eur))
    .slice(0, 2);

  const hasSize = (size: number) => recs.some(rec => rec.package_option_id === size);

  // "1 next level": the next size up (fall back to the size below if already at the top).
  const nextTier = tiers[currentIdx + 1] || tiers[currentIdx - 1];
  if (nextTier && !hasSize(nextTier.package_option_id)) {
    recs.push(nextTier);
  }

  // If a size only had one usable variation, fill toward higher tiers (then lower) to reach 3.
  for (let i = currentIdx + 2; i < tiers.length && recs.length < RECOMMENDATION_COUNT; i++) {
    if (!hasSize(tiers[i].package_option_id)) recs.push(tiers[i]);
  }
  for (let i = currentIdx - 1; i >= 0 && recs.length < RECOMMENDATION_COUNT; i--) {
    if (!hasSize(tiers[i].package_option_id)) recs.push(tiers[i]);
  }

  const smallTier = tiers.find(isSmallTier);
  if (smallTier && !recs.some(isSmallTier)) {
    if (recs.length < RECOMMENDATION_COUNT) {
      recs.push(smallTier);
    } else {
      recs[recs.length - 1] = smallTier;
    }
  }

  return sortTrainingPackagesByPrice(recs.slice(0, RECOMMENDATION_COUNT));
};

// A membership top-up is purchased from the regular package catalog, but its size
// may not exceed the studio's largest membership plan.
export const buildMembershipTopups = (
  packages: PackageCatalogItem[],
  memberships: PackageCatalogItem[]
): PackageCatalogItem[] => {
  if (memberships.length === 0) {
    return [];
  }

  const maximumMembershipCoins = Math.max(...memberships.map(item => item.coins_amount));
  return packages
    .filter(item => item.coins_amount <= maximumMembershipCoins)
    .sort((a, b) => a.coins_amount - b.coins_amount || Number(a.price_eur) - Number(b.price_eur));
};

export const EXTRA_COINS_DESCRIPTION = 'These coins will be added to your current package.';
export const EXTRA_COINS_PURCHASE_EXPLANATION =
  'This is a one-time purchase of extra coins. It does not change your membership plan or monthly billing.';

export const packageCoinSummary = (item: Pick<PackageCatalogItem, 'coins_amount' | 'private_coins_amount' | 'is_private_class_package'>): string => {
  const parts: string[] = [];
  const privateCoins = (item.private_coins_amount || 0) > 0 ? `${formatCoins(item.private_coins_amount)} PȻ` : null;
  const danceCoins = item.coins_amount > 0 ? `${item.is_private_class_package ? '' : 'Dance '}Ȼ ${formatCoins(item.coins_amount)}` : null;
  (item.is_private_class_package ? [privateCoins, danceCoins] : [danceCoins, privateCoins])
    .forEach(part => { if (part) parts.push(part); });
  return parts.join(' · ') || 'No coins';
};

export const PackageVariationDescription: React.FC<{ description?: string | null }> = ({ description }) => {
  const visibleDescription = description?.trim();
  return visibleDescription
    ? <p className="pm-variation-description">{visibleDescription}</p>
    : null;
};

interface TopupConfirmationDialogProps {
  item: PackageCatalogItem;
  busy: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}

export const TopupConfirmationDialog: React.FC<TopupConfirmationDialogProps> = ({ item, busy, onCancel, onConfirm }) => (
  <div className="pm-dialog-backdrop" role="presentation" onMouseDown={busy ? undefined : onCancel}>
    <section
      className="pm-dialog pm-topup-confirm-dialog"
      role="dialog"
      aria-modal="true"
      aria-labelledby="pm-topup-confirm-title"
      aria-describedby="pm-topup-confirm-description"
      onMouseDown={event => event.stopPropagation()}
    >
      <h2 id="pm-topup-confirm-title">Confirm Extra Coins</h2>
      <dl className="pm-confirm-details">
        <div>
          <dt>Coins to Add</dt>
          <dd>{packageCoinSummary(item)}</dd>
        </div>
        <div>
          <dt>Price</dt>
          <dd>{formatPrice(item.price_eur)}</dd>
        </div>
      </dl>
      <p id="pm-topup-confirm-description">{EXTRA_COINS_DESCRIPTION}</p>
      <div className="pm-dialog-actions">
        <button className="pm-ghost-button" type="button" disabled={busy} onClick={onCancel}>
          Cancel
        </button>
        <button className="pm-primary-button" type="button" disabled={busy} onClick={onConfirm}>
          {busy ? 'Opening…' : 'Continue to Payment'}
        </button>
      </div>
    </section>
  </div>
);


const PackagesMembership = observer(() => {
  const store = useContext(RootStoreContext);
  const navigate = useNavigate();
  const user = store.registrationStore.user;
  const pendingEarlyBirdPackage = user?.pendingEarlyBirdPackage;
  const [catalog, setCatalog] = useState<PackagesCatalogResponse>({ training_packages: [], memberships: [] });
  const [status, setStatus] = useState<PackagesStatusResponse | null>(null);
  const [activeTab, setActiveTab] = useState<ActiveTab>(() => hasPrivateClassResume() || new URLSearchParams(window.location.search).get('tab') === 'private' ? 'private' : 'membership');
  const [privateClassPurchaseFlow, setPrivateClassPurchaseFlow] = useState(hasPrivateClassResume);
  const cancelPrivateClassSelection = () => {
    try { sessionStorage.removeItem(PRIVATE_CLASS_RESUME_STORAGE_KEY); } catch { /* Storage may be blocked. */ }
    setPrivateClassPurchaseFlow(false);
    setActiveTab('membership');
    setShowAll(false);
    window.history.replaceState(window.history.state, '', window.location.pathname);
  };
  const [showYouthPackages, setShowYouthPackages] = useState(false);
  const [attendanceStats, setAttendanceStats] = useState<AttendanceStats | null>(null);
  const [showAll, setShowAll] = useState(false);
  const [loading, setLoading] = useState(false);
  const [busyAction, setBusyAction] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [bootstrapping, setBootstrapping] = useState(true);
  const [showTierOptions, setShowTierOptions] = useState(false);
  const [tierToConfirm, setTierToConfirm] = useState<PackageCatalogItem | null>(null);
  const [showCancellationConfirmation, setShowCancellationConfirmation] = useState(false);
  const [showTopupDialog, setShowTopupDialog] = useState(false);
  const [topupToConfirm, setTopupToConfirm] = useState<PackageCatalogItem | null>(null);
  const [cardReturn] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return { outcome: params.get('card_update'), sessionId: params.get('session_id') };
  });
  const [statusLoaded, setStatusLoaded] = useState(false);
  const [resumePrivateClassAfterCheckout, setResumePrivateClassAfterCheckout] = useState(false);

  const handleBack = () => navigate(
    hasPrivateClassResume() ? '/private-classes?resume=1' : '/',
    { replace: true }
  );

  // On a hard load of this page — most importantly the Stripe checkout redirect back to
  // /packages-membership?checkout=... — only the JWT is restored on boot, not the
  // registrationStore.user object. Re-hydrate it from the token before falling back to the
  // signed-out view below, otherwise a paying user lands on "Please sign in".
  useEffect(() => {
    let active = true;
    (async () => {
      try {
        await store.registrationStore.ensureUserLoaded();
      } catch (e) {
        // Ignore — fall back to the signed-out state below.
      } finally {
        if (active) {
          setBootstrapping(false);
        }
      }
    })();
    return () => {
      active = false;
    };
  }, [store]);

  useEffect(() => {
    if (!user?.id) {
      return;
    }
    setStatusLoaded(false);
    if (cardReturn.outcome !== 'success') load();
  }, [user?.id, cardReturn.outcome]);

  // Surface the result when Stripe redirects back, then strip the query param so a
  // refresh doesn't repeat it.
  useEffect(() => {
    const checkout = new URLSearchParams(window.location.search).get('checkout');
    if (checkout === 'success') {
      if (hasPrivateClassResume()) {
        setResumePrivateClassAfterCheckout(true);
      } else {
        setMessage('Payment received. Your package will appear here shortly.');
      }
    } else if (checkout === 'cancel') {
      setError('Checkout was cancelled — you have not been charged.');
    }
    if (checkout) {
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  useEffect(() => {
    if (resumePrivateClassAfterCheckout && user?.id) {
      navigate('/private-classes?checkout=success&resume=1', { replace: true });
    }
  }, [navigate, resumePrivateClassAfterCheckout, user?.id]);

  const load = async () => {
    if (!user?.id) {
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const [catalogResponse, statusResponse, progress] = await Promise.all([
        getPackagesCatalog(user.id),
        getPackagesStatus(user.id),
        getStudentProgress(user.id).catch(() => null)
      ]);
      setCatalog(catalogResponse);
      setStatus(statusResponse);
      window.dispatchEvent(new Event('private-coins-changed'));
      setStatusLoaded(true);
      setAttendanceStats(progress?.attendanceStats ?? null);
    } catch (e: any) {
      setError(errorText(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!user?.id || !cardReturn.outcome) return;
    let active = true;
    const clearReturnParams = () => {
      const url = new URL(window.location.href);
      url.searchParams.delete('card_update');
      url.searchParams.delete('session_id');
      window.history.replaceState(window.history.state, '', url.pathname + url.search);
    };
    if (cardReturn.outcome === 'cancel') {
      setMessage('Card change cancelled. Your saved card has not changed.');
      clearReturnParams();
      return;
    }
    if (cardReturn.outcome !== 'success') return;
    setBusyAction('card');
    (async () => {
      try {
        if (!cardReturn.sessionId) throw new Error('Card setup session is missing. Please try changing your card again.');
        const result = await completeMembershipCardSetup(user.id, cardReturn.sessionId);
        if (!result.updated) throw new Error('Card update could not be confirmed. Please try again.');
        if (active) {
          await load();
          setMessage('Your membership card has been updated. Future membership payments will use this card.');
        }
      } catch (e: any) {
        if (active) {
          await load();
          setError(cardErrorText(e));
        }
      } finally {
        if (active) {
          setBusyAction(null);
          clearReturnParams();
        }
      }
    })();
    return () => { active = false; };
  }, [user?.id, cardReturn]);

  const startCardChange = async () => {
    if (!user?.id) return;
    setBusyAction('card');
    setError(null);
    setMessage(null);
    try {
      const result = await createMembershipCardSetup(user.id);
      if (!result.checkout_url) throw new Error('Card setup link was not returned. Please try again.');
      window.location.assign(result.checkout_url);
    } catch (e: any) {
      setError(cardErrorText(e));
    } finally {
      setBusyAction(null);
    }
  };

  const showYouthToggle = shouldShowYouthPackageToggle(user);

  // The toggle selects exactly one catalog group at a time. Packages and membership
  // plans are then displayed from highest to lowest price.
  const availableItems = useMemo(() => {
    const catalogItems = packagesForTab(catalog, activeTab);
    const items = activeTab === 'private' ? catalogItems
      : filterPackagesByYouth(catalogItems, showYouthToggle && showYouthPackages);
    return sortTrainingPackagesByPrice(items);
  }, [activeTab, catalog.training_packages, catalog.memberships, showYouthPackages, showYouthToggle]);

  const weeklyRate = useMemo(() => recentWeeklyRate(attendanceStats), [attendanceStats]);

  const hasActivePackage = !!status?.active_package;
  const defaultPackageName = store.studioConfigStore.defaultTrainingPackageRecommendation;

  // Students with an active package get attendance-based recommendations. Students
  // without one get the configured real coin package plus the next two larger tiers,
  // never an extender.
  const recommendedItems = useMemo(
    () => buildRecommendations(availableItems, weeklyRate, hasActivePackage, defaultPackageName),
    [availableItems, weeklyRate, hasActivePackage, defaultPackageName]
  );

  // Payment status of the student's current training package, mirroring the header's
  // PAID/NOT PAID pill (status_paid / status_closed => paid) so both screens agree.
  // null means unknown — no active package in the store — so the badge is hidden.
  const packagePaid = useMemo<boolean | null>(() => {
    const pkg = user?.latestTrainingPackage;
    if (!pkg || !pkg.isActive) {
      return null;
    }
    if (status?.active_package && status.active_package.id !== pkg.id) {
      return null;
    }
    return pkg.packageStatus === 'status_paid' || pkg.packageStatus === 'status_closed';
  }, [status?.active_package, user?.latestTrainingPackage]);

  const activeMembership = useMemo(() => {
    if (!status?.membership || !isActiveMembershipStatus(status.membership.status)) {
      return null;
    }
    return status.membership;
  }, [status?.membership]);


  // The status endpoint is authoritative, but the terminal user payload is a useful
  // fallback while Rails and the terminal API are being refreshed independently.
  const currentTrainingPackage = useMemo<ActivePackageStatus | null>(() => {
    if (status?.active_package) {
      return status.active_package;
    }
    const pkg = user?.latestTrainingPackage;
    if (!pkg?.isActive || pkg.isSinglePackage) {
      return null;
    }
    return {
      id: pkg.id,
      name: pkg.packageName,
      available_coins: pkg.availableCoins,
      end_date: pkg.endDate.toString()
    };
  }, [status?.active_package, user?.latestTrainingPackage]);

  const startCheckout = async (item: PackageCatalogItem, membership: boolean) => {
    if (!user?.id) {
      return;
    }
    const actionId = `${membership ? 'membership' : 'training'}-${item.package_variation_id}`;
    setBusyAction(actionId);
    setError(null);
    setMessage(null);
    try {
      const requestId = newRequestId();
      const result = membership
        ? await createMembershipCheckout(user.id, item.package_variation_id, requestId)
        : await createTrainingPackageCheckout(user.id, item.package_variation_id, requestId);
      if (!result.checkout_url) {
        throw new Error('Checkout link was not returned.');
      }
      window.location.assign(result.checkout_url);
    } catch (e: any) {
      setError(errorText(e));
    } finally {
      setBusyAction(null);
    }
  };

  const requestMembershipChange = async (item: PackageCatalogItem) => {
    if (!user?.id) {
      return;
    }
    setBusyAction(`change-${item.package_variation_id}`);
    setError(null);
    setMessage(null);
    try {
      const keepingCurrentTier = item.package_variation_id === activeMembership?.package_variation_id
        && isTierChangeScheduled(status);
      await changeMembership(user.id, item.package_variation_id);
      setMessage(keepingCurrentTier
        ? 'Pending plan change removed. Your current plan will continue.'
        : 'Membership change scheduled for the next billing cycle.');
      setTierToConfirm(null);
      setShowTierOptions(false);
      await load();
    } catch (e: any) {
      setError(errorText(e));
    } finally {
      setBusyAction(null);
    }
  };

  const startMembershipTopup = async (item: PackageCatalogItem) => {
    if (!user?.id) {
      return;
    }
    const actionId = `topup-${item.package_variation_id}`;
    setBusyAction(actionId);
    setError(null);
    setMessage(null);
    try {
      const result = await createMembershipTopupCheckout(
        user.id,
        item.package_variation_id,
        newRequestId()
      );
      if (!result.checkout_url) {
        throw new Error('Checkout link was not returned.');
      }
      window.location.assign(result.checkout_url);
    } catch (e: any) {
      setTopupToConfirm(null);
      setShowTopupDialog(false);
      setError(errorText(e));
    } finally {
      setBusyAction(null);
    }
  };

  const requestCancel = async () => {
    if (!user?.id) {
      return;
    }
    setShowCancellationConfirmation(false);
    setBusyAction('cancel');
    setError(null);
    setMessage(null);
    try {
      await cancelMembership(user.id);
      setMessage('Membership cancellation scheduled.');
      await load();
    } catch (e: any) {
      setError(errorText(e));
    } finally {
      setBusyAction(null);
    }
  };

  const requestKeepActive = async () => {
    if (!user?.id) {
      return;
    }
    setBusyAction('keep-active');
    setError(null);
    setMessage(null);
    try {
      await keepMembershipActive(user.id);
      setMessage('Membership will stay active.');
      await load();
    } catch (e: any) {
      setError(errorText(e));
    } finally {
      setBusyAction(null);
    }
  };

  const requestEarlyBirdActivation = async () => {
    if (!pendingEarlyBirdPackage?.canActivateEarlyBird) {
      return;
    }

    const packageId = pendingEarlyBirdPackage.id;
    const packageName = pendingEarlyBirdPackage.packageName;
    setBusyAction('activate-early-bird');
    setError(null);
    setMessage(null);
    try {
      await store.registrationStore.activateEarlyBirdPackage(packageId);
      setMessage(`${packageName} activated. Your coins are ready to use.`);
      await load();
    } catch (e: any) {
      if (rawErrorCode(e).toUpperCase().includes('PACKAGE_ALREADY_ACTIVE')) {
        setMessage(`${packageName} is already active. Your coins are ready to use.`);
        await load();
        return;
      }
      setError(errorText(e));
    } finally {
      setBusyAction(null);
    }
  };

  const renderPackageCard = (item: PackageCatalogItem, membership: boolean, opts?: { recommended?: boolean }) => {
    const currentMembership = activeMembership;
    const isCurrent = membership && currentMembership?.package_variation_id === item.package_variation_id;
    const hasMembership = membership && !!currentMembership;
    const actionId = `${membership ? 'membership' : 'training'}-${item.package_variation_id}`;
    const changeActionId = `change-${item.package_variation_id}`;
    const isRecommended = !!opts?.recommended;
    const canChange = hasMembership && !isCurrent;

    return (
      <article className={`pm-card ${isRecommended ? 'pm-card-recommended' : ''}`} key={`${item.package_variation_id}-${membership}`}>
        <div className="pm-card-header">
          <div className="pm-card-title">
            <h3>{getPackageCardTitle(item, membership)}</h3>
            {item.subname?.trim() && (
              <span className="pm-card-subname">{item.subname.trim()}</span>
            )}
          </div>
          {isCurrent && <span className="pm-chip pm-chip-current">Current</span>}
        </div>

        <div className="pm-price">
          {formatPrice(item.price_eur)}
          {membership && <span className="pm-price-period">/ month</span>}
        </div>


        <div className="pm-card-details">
          <div className={`pm-detail-row ${membership ? 'pm-detail-row-primary' : ''}`}>
            <span className="pm-detail-label">{membership ? 'You get' : 'Coins'}</span>
            <div className="pm-detail-value-group">
              <span className="pm-detail-value">
                {packageCoinSummary(item)}{membership && <span className="pm-detail-period"> / month</span>}
              </span>
              
            </div>
          </div>
          {!membership && (
            <div className="pm-detail-row">
              <span className="pm-detail-label">Valid for</span>
              <span className="pm-detail-value">{formatValidityMonths(item.validity_period_days)}</span>
            </div>
          )}
        </div>

        <div className="pm-card-footer">
          <PackageVariationDescription description={item.description} />

          <div className="pm-card-actions">
            {!membership && (
              <button
                className="pm-primary-button"
                disabled={busyAction === actionId}
                onClick={() => startCheckout(item, false)}
              >
                {busyAction === actionId ? 'Opening...' : 'Buy package'}
              </button>
            )}
            {membership && !hasMembership && (
              <button
                className="pm-primary-button"
                disabled={busyAction === actionId}
                onClick={() => startCheckout(item, true)}
              >
                {busyAction === actionId ? 'Opening...' : 'Choose This Plan'}
              </button>
            )}
            {membership && canChange && (
              <button
                className="pm-secondary-button"
                disabled={busyAction === changeActionId}
                onClick={() => requestMembershipChange(item)}
              >
                {busyAction === changeActionId ? 'Saving...' : changeLabel(item)}
              </button>
            )}
          </div>
        </div>
      </article>
    );
  };

  const changeLabel = (item: PackageCatalogItem) => {
    const currentCoins = status?.membership?.monthly_coins || 0;
    return item.coins_amount >= currentCoins ? 'Upgrade next cycle' : 'Downgrade next cycle';
  };

  const renderFeedbackDialog = () => {
    const text = error || message;
    if (!text) {
      return null;
    }
    const failed = !!error;
    const close = () => failed ? setError(null) : setMessage(null);
    return (
      <div className="pm-dialog-backdrop" role="presentation" onMouseDown={close}>
        <section
          className={`pm-dialog pm-feedback-dialog ${failed ? 'pm-feedback-error' : 'pm-feedback-success'}`}
          role="alertdialog"
          aria-modal="true"
          aria-labelledby="pm-feedback-title"
          aria-describedby="pm-feedback-message"
          onMouseDown={event => event.stopPropagation()}
        >
          <h2 id="pm-feedback-title">{failed ? 'Action unsuccessful' : 'Success'}</h2>
          <p id="pm-feedback-message">{text}</p>
          <div className="pm-dialog-actions">
            <button className="pm-primary-button" type="button" autoFocus onClick={close}>OK</button>
          </div>
        </section>
      </div>
    );
  };

  if (!user) {
    return (
      <main className="pm-page">
        <div className="pm-empty">{bootstrapping ? 'Loading…' : 'Please sign in to manage packages.'}</div>
      </main>
    );
  }

  // Do not render the non-membership purchase tabs until the authoritative
  // membership status has returned. Otherwise existing members briefly see
  // controls that they are not allowed to use while the page is loading.
  if (!statusLoaded) {
    return (
      <main className="pm-page">
        <div className="pm-toolbar">
          <button className="secondary-nav-button" type="button" onClick={handleBack}>
            <FontAwesomeIcon icon={faChevronLeft} />
            <span>Back</span>
          </button>
          <h1>Packages &amp; Membership</h1>
        </div>
        <div className="pm-empty">
          {loading ? 'Loading packages & membership…' : 'Packages & Membership could not be loaded.'}
        </div>
        {renderFeedbackDialog()}
      </main>
    );
  }

  const renderPackageTabs = () => (
    <div className="pm-tabs" role="tablist" aria-label="Package type">
      {([['membership', 'Membership Plans'], ['training', 'Training Packages'], ['private', 'Private Classes']] as const).map(([tab, title]) => (
        <button key={tab} type="button" role="tab" aria-selected={activeTab === tab}
          className={activeTab === tab ? 'active' : ''} onClick={() => { setActiveTab(tab); setShowAll(false); }}>{title}</button>
      ))}
    </div>
  );
  const renderSavedSelection = () => privateClassPurchaseFlow && (
    <div className="pm-alert pm-alert-success" role="status">
      Your private-class selection is saved. Choose a package that includes Private Coins, then complete payment to return to your booking.
      <button type="button" className="pm-ghost-button" onClick={cancelPrivateClassSelection}>Cancel Private Class Selection</button>
    </div>
  );

  const isMembershipTab = activeTab === 'membership';
  const trainingPackageDisplay = getTrainingPackageDisplay(availableItems, recommendedItems, showAll);
  const hasMoreTrainingPackages = availableItems.length > recommendedItems.length;
  const renderShowRecommendedOnlyButton = () => (
    <div className="pm-showall">
      <button
        className="pm-showall-toggle"
        type="button"
        onClick={() => setShowAll(false)}
        aria-expanded={true}
      >
        Show Recommended Only
        <FontAwesomeIcon icon={faChevronUp} />
      </button>
    </div>
  );
  const cancellationScheduled = isCancellationScheduled(status);
  const currentMembershipTier = activeMembership
    ? catalog.memberships.find(item => item.package_variation_id === activeMembership.package_variation_id)
    : undefined;
  const membershipTiers = activeMembership
    ? buildTiers(catalog.memberships)
      .map(item => item.package_option_id === activeMembership.package_option_id && currentMembershipTier
        ? currentMembershipTier
        : item)
      .sort((a, b) => a.coins_amount - b.coins_amount || Number(a.price_eur) - Number(b.price_eur))
    : [];
  const pendingMembershipTier = isTierChangeScheduled(status)
    ? catalog.memberships.find(item =>
      item.name === activeMembership?.pending_tier_name
      && (!activeMembership?.pending_variation_name || item.variation_name === activeMembership.pending_variation_name))
    : undefined;
  const membershipActionsAllowed = activeMembership?.status.toLowerCase() === 'active';
  const membershipTopups = activeMembership
    ? buildMembershipTopups(catalog.training_packages.filter(item => !item.is_private_class_package), catalog.memberships)
    : [];
  const membershipCancellationAllowed = !!activeMembership
    && (activeMembership.cancel_allowed || activeMembership.status.toLowerCase() === 'past_due');

  if (activeMembership && isMembershipTab) {
    return (
      <main className="pm-page">
        <div className="pm-toolbar">
          <button className="secondary-nav-button" type="button" onClick={handleBack}>
            <FontAwesomeIcon icon={faChevronLeft} />
            <span>Back</span>
          </button>
          <h1>My Membership Plan</h1>
          {membershipActionsAllowed && (
            <button
              className="pm-topup-button"
              type="button"
              onClick={() => setShowTopupDialog(true)}
            >
              Get Extra Coins
            </button>
          )}
        </div>

        {renderPackageTabs()}
        {renderSavedSelection()}

        <section className="pm-membership" aria-label="My current plan">
          <div className="pm-active-head">
            <div className="pm-membership-heading">
              <span className="pm-active-title">My Current Plan</span>
              <strong>{activeMembership.tier_name}</strong>
            </div>
            <span className={`pm-status-badge pm-status-${membershipTone(activeMembership.status)}`}>
              {prettyStatus(activeMembership.status)}
            </span>
          </div>

          <div className="pm-membership-summary">
            <div className="pm-membership-detail pm-membership-balance">
              <span className="pm-membership-detail-label">Dance Coins</span>
              <strong>
                {status?.active_package ? `Ȼ ${formatCoins(status.active_package.available_coins)}` : '—'}
              </strong>
            </div>
            <div className="pm-membership-detail pm-membership-balance">
              <span className="pm-membership-detail-label">Private Coins</span>
              <strong>{formatCoins(status?.private_coin_balance)} PȻ</strong>
              <PrivateCoinExpiry buckets={status?.private_coin_buckets} />
            </div>
            <div className="pm-membership-detail">
              <span className="pm-membership-detail-label">Validity Date</span>
              <strong>
                {status?.active_package?.end_date ? formatMembershipValidityDate(status.active_package.end_date) : '—'}
              </strong>
            </div>
            <div className="pm-membership-detail">
              <span className="pm-membership-detail-label">Next Renewal Date</span>
              <strong>{activeMembership.next_billing_at ? formatDate(activeMembership.next_billing_at) : '—'}</strong>
            </div>
            <div className="pm-membership-detail">
              <span className="pm-membership-detail-label">You Get</span>
              <strong>Dance Ȼ {formatCoins(activeMembership.monthly_coins)}</strong>
              {(activeMembership.monthly_private_coins || 0) > 0 && (
                <span className="pm-membership-detail-sub">{formatCoins(activeMembership.monthly_private_coins)} PȻ</span>
              )}
            </div>
            <div className="pm-membership-detail">
              <span className="pm-membership-detail-label">Price</span>
              <strong>{currentMembershipTier ? formatPrice(currentMembershipTier.price_eur) : '—'}</strong>
            </div>
          </div>

          <section className="pm-membership-card" aria-labelledby="pm-payment-card-title">
            <div>
              <h2 id="pm-payment-card-title">Membership payment card</h2>
              <p className="pm-saved-card">
                {activeMembership.payment_card
                  ? `${activeMembership.payment_card.brand.toUpperCase()} •••• ${activeMembership.payment_card.last4}`
                  : 'Card details unavailable'}
              </p>
              <p>Change the card used for future membership payments. Saving a card does not charge it.</p>
            </div>
            <button className="pm-secondary-button" type="button" disabled={!!busyAction} onClick={startCardChange}>
              {busyAction === 'card' ? 'Opening…' : 'Change card'}
            </button>
          </section>

          {isTierChangeScheduled(status) && (
            <section className="pm-membership-upcoming" aria-labelledby="pm-upcoming-change-title">
              <div className="pm-membership-upcoming-heading">
                <div>
                  <span className="pm-membership-upcoming-kicker">Upcoming change</span>
                  <h2 id="pm-upcoming-change-title">
                    {pendingMembershipTier?.display_name || activeMembership.pending_tier_name || 'New membership plan'}
                  </h2>
                </div>
                <span className="pm-membership-upcoming-date">
                  Starts {activeMembership.pending_effective_at || activeMembership.next_billing_at
                    ? formatDate(activeMembership.pending_effective_at || activeMembership.next_billing_at!)
                    : 'next cycle'}
                </span>
              </div>
              <div className="pm-membership-upcoming-details">
                <div>
                  <span>You Get</span>
                  <strong>{pendingMembershipTier ? `Ȼ ${formatCoins(pendingMembershipTier.coins_amount)}` : '—'}</strong>
                </div>
                <div>
                  <span>Price</span>
                  <strong>{pendingMembershipTier ? formatPrice(pendingMembershipTier.price_eur) : '—'}</strong>
                </div>
              </div>
            </section>
          )}

          {activeMembership.pending_action === 'cancel' && (
            <div className="pm-membership-pending" role="status">
              {membershipStatusText(status)}
            </div>
          )}

          {!membershipActionsAllowed && (
            <div className="pm-membership-payment-issue" role="alert">
              Your membership payment needs attention. You can change your payment card above. Coins top-up and plan changes are unavailable until the membership is active again.
            </div>
          )}

          <div className="pm-membership-management">
            {membershipActionsAllowed && (
              <>
                <div className="pm-membership-management-head">
                  <div>
                    <h2>Change My Plan</h2>
                    <p>Upgrade or downgrade from your next billing cycle.</p>
                  </div>
                  <button
                    className="pm-secondary-button"
                    type="button"
                    aria-expanded={showTierOptions}
                    onClick={() => setShowTierOptions(value => !value)}
                  >
                    {showTierOptions ? 'Hide plan options' : 'Change My Plan'}
                  </button>
                </div>

                {showTierOptions && membershipTiers.length > 0 ? (
                  <div className="pm-tier-options">
                    {membershipTiers.map(item => {
                      const actionId = `change-${item.package_variation_id}`;
                      const isCurrentTier = item.package_variation_id === activeMembership.package_variation_id;
                      const isPendingTier = activeMembership.pending_action !== 'cancel'
                        && activeMembership.pending_tier_name === item.name
                        && (!activeMembership.pending_variation_name || activeMembership.pending_variation_name === item.variation_name);
                      const canRestoreCurrentTier = isCurrentTier && isTierChangeScheduled(status);
                      return (
                        <article className="pm-tier-option" key={item.package_variation_id}>
                          <div className="pm-tier-option-copy">
                            <strong>{item.display_name || item.name}</strong>
                            <div className="pm-tier-option-summary">
                              <span>Ȼ {formatCoins(item.coins_amount)} per month</span>
                              <span>{formatPrice(item.price_eur)}</span>
                            </div>
                            {item.membership_auto_topup_coins_threshold !== undefined && (
                              <span>
                                If your balance goes below {formatCoins(item.membership_auto_topup_coins_threshold)} coins,
                                we’ll add coins automatically.
                              </span>
                            )}
                            {item.max_coins_rollover !== undefined && (
                              <span>
                                Up to {formatCoins(item.max_coins_rollover)} unused coins will roll over to the next month.
                              </span>
                            )}
                          </div>
                          <button
                            className="pm-secondary-button"
                            disabled={busyAction === actionId || isPendingTier || cancellationScheduled || (isCurrentTier && !canRestoreCurrentTier)}
                            onClick={() => setTierToConfirm(item)}
                          >
                            {isPendingTier
                              ? pendingTierLabel(activeMembership)
                              : isCurrentTier
                                ? (canRestoreCurrentTier ? 'Keep current plan' : 'Current plan')
                                : 'Switch to This Plan'}
                          </button>
                        </article>
                      );
                    })}
                  </div>
                ) : showTierOptions ? (
                  <p className="pm-membership-no-tiers">No other membership plans are available.</p>
                ) : null}
              </>
            )}

            <div className="pm-membership-cancel">
              {cancellationScheduled ? (
                <button
                  className="pm-ghost-button pm-ghost-keep"
                  disabled={busyAction === 'keep-active'}
                  onClick={requestKeepActive}
                >
                  {busyAction === 'keep-active' ? 'Saving...' : 'Keep membership active'}
                </button>
              ) : membershipCancellationAllowed ? (
                <button
                  className="pm-ghost-button pm-ghost-danger"
                  disabled={busyAction === 'cancel'}
                  onClick={() => setShowCancellationConfirmation(true)}
                >
                  Cancel Membership
                </button>
              ) : activeMembership.cancel_after_at ? (
                <span className="pm-status-cancel-note">
                  Cancellation will be available from {formatDate(activeMembership.cancel_after_at)}.
                </span>
              ) : null}
            </div>
          </div>
        </section>

        {showCancellationConfirmation && (
          <div className="pm-dialog-backdrop" role="presentation" onMouseDown={() => setShowCancellationConfirmation(false)}>
            <section
              className="pm-dialog"
              role="alertdialog"
              aria-modal="true"
              aria-labelledby="pm-cancel-membership-title"
              aria-describedby="pm-cancel-membership-description"
              onMouseDown={event => event.stopPropagation()}
            >
              <h2 id="pm-cancel-membership-title">Cancel Membership?</h2>
              <p id="pm-cancel-membership-description">
                Are you sure you want to cancel your membership? This will schedule your membership cancellation.
              </p>
              <div className="pm-dialog-actions">
                <button
                  className="pm-primary-button"
                  type="button"
                  autoFocus
                  onClick={() => setShowCancellationConfirmation(false)}
                >
                  Keep Membership
                </button>
                <button
                  className="pm-ghost-button pm-ghost-danger"
                  type="button"
                  onClick={requestCancel}
                >
                  Cancel Membership
                </button>
              </div>
            </section>
          </div>
        )}

        {showTopupDialog && (
          <div className="pm-dialog-backdrop" role="presentation" onMouseDown={() => setShowTopupDialog(false)}>
            <section
              className="pm-dialog pm-topup-dialog"
              role="dialog"
              aria-modal="true"
              aria-labelledby="pm-topup-title"
              aria-describedby="pm-topup-description"
              onMouseDown={event => event.stopPropagation()}
            >
              <h2 id="pm-topup-title">Get Extra Coins</h2>
              <p id="pm-topup-description">{EXTRA_COINS_PURCHASE_EXPLANATION}</p>
              {membershipTopups.length > 0 ? (
                <div className="pm-topup-options" aria-label="Extra coin packages">
                  {membershipTopups.map(item => (
                      <button
                        key={item.package_variation_id}
                        type="button"
                        disabled={busyAction !== null}
                        onClick={() => {
                          setShowTopupDialog(false);
                          setTopupToConfirm(item);
                        }}
                      >
                        <span>
                          <strong>{item.display_name || item.name}</strong>
                          <small>{packageCoinSummary(item)}</small>
                        </span>
                        <strong>{formatPrice(item.price_eur)}</strong>
                      </button>
                  ))}
                </div>
              ) : (
                <p className="pm-topup-empty">No extra coin packages are currently available.</p>
              )}
              <div className="pm-dialog-actions">
                <button
                  className="pm-ghost-button"
                  type="button"
                  disabled={busyAction !== null}
                  onClick={() => setShowTopupDialog(false)}
                >
                  Cancel
                </button>
              </div>
            </section>
          </div>
        )}

        {topupToConfirm && (
          <TopupConfirmationDialog
            item={topupToConfirm}
            busy={busyAction === `topup-${topupToConfirm.package_variation_id}`}
            onCancel={() => setTopupToConfirm(null)}
            onConfirm={() => startMembershipTopup(topupToConfirm)}
          />
        )}

        {tierToConfirm && (
          <div className="pm-dialog-backdrop" role="presentation" onMouseDown={() => setTierToConfirm(null)}>
            <section
              className="pm-dialog"
              role="dialog"
              aria-modal="true"
              aria-labelledby="pm-tier-confirm-title"
              onMouseDown={event => event.stopPropagation()}
            >
              <h2 id="pm-tier-confirm-title">
                {tierToConfirm.package_variation_id === activeMembership.package_variation_id
                  ? 'Keep current plan?'
                  : 'Confirm Your New Plan'}
              </h2>
              <p>
                {tierToConfirm.package_variation_id === activeMembership.package_variation_id ? (
                  <>Your pending plan change will be removed and <strong>{activeMembership.tier_name}</strong> will continue.</>
                ) : (
                  <>Your membership will switch from <strong>{currentMembershipTier?.display_name || activeMembership.tier_name}</strong> to{' '}
                    <strong>{tierToConfirm.display_name || tierToConfirm.name}</strong> on{' '}
                    <strong>{activeMembership.next_billing_at ? formatDialogDate(activeMembership.next_billing_at) : 'your next billing date'}</strong>.{' '}
                    Until then, your current plan and benefits remain the same. Starting on that date, you’ll receive{' '}
                    <strong>{formatCoins(tierToConfirm.coins_amount)} coins</strong> per month and be charged{' '}
                    <strong>{formatPrice(tierToConfirm.price_eur)}</strong> per month.</>
                )}
              </p>
              <div className="pm-dialog-actions">
                <button className="pm-ghost-button" type="button" disabled={busyAction !== null} onClick={() => setTierToConfirm(null)}>
                  Cancel
                </button>
                <button
                  className="pm-primary-button"
                  type="button"
                  disabled={busyAction !== null}
                  onClick={() => requestMembershipChange(tierToConfirm)}
                >
                  {busyAction ? 'Confirming…' : tierToConfirm.package_variation_id === activeMembership.package_variation_id
                    ? 'Keep current plan'
                    : 'Confirm Change'}
                </button>
              </div>
            </section>
          </div>
        )}
        {renderFeedbackDialog()}
      </main>
    );
  }

  return (
    <main className="pm-page">
      <div className="pm-toolbar">
        <button className="secondary-nav-button" type="button" onClick={handleBack}>
          <FontAwesomeIcon icon={faChevronLeft} />
          <span>Back</span>
        </button>
        <h1>Packages &amp; Membership</h1>
      </div>

      <section className="pm-active pm-training-summary" aria-label="Current training package">
          <div className="pm-active-head">
            <span className="pm-active-title">Current training package</span>
            {currentTrainingPackage && packagePaid !== null && (
              <span className={`pm-pay-badge ${packagePaid ? 'pm-pay-ok' : 'pm-pay-bad'}`}>
                {packagePaid ? 'Paid' : 'Not paid'}
              </span>
            )}
          </div>
          {currentTrainingPackage ? (
            <div className="pm-training-summary-body">
              <div className="pm-training-summary-name">{currentTrainingPackage.name || 'Training package'}</div>
              <div className="pm-training-summary-stat">
                <span className="pm-training-summary-value">Ȼ {formatCoins(currentTrainingPackage.available_coins)}</span>
                <span className="pm-training-summary-label">Remaining coins</span>
              </div>
              <div className="pm-training-summary-stat">
                <span className="pm-training-summary-value pm-training-summary-date">
                  {currentTrainingPackage.end_date ? coinValidityDate(currentTrainingPackage.end_date) : 'No expiry'}
                </span>
                <span className="pm-training-summary-label">Valid until</span>
              </div>
            </div>
          ) : (
            <div className="pm-active-empty">
              <span className="pm-active-empty-text">You don't have an active training package.</span>
            </div>
          )}
      </section>
      <section className="pm-active" aria-label="Private Coin balance">
        <div className="pm-active-head">
          <span className="pm-active-title">Private classes</span>
        </div>
        <div className="pm-active-body">
          <div className="pm-active-coins">
            <span className="pm-active-coins-value">{formatCoins(status?.private_coin_balance)} PȻ</span>
            <span className="pm-active-coins-label">Private Coins</span>
            <PrivateCoinExpiry buckets={status?.private_coin_buckets} />
          </div>
          <div className="pm-active-facets">
            <div className="pm-active-facet">
              <span className="pm-active-facet-label">Use</span>
              <span className="pm-active-facet-value">Private class bookings</span>
              <span className="pm-active-facet-sub">Private Coins are separate from Dance Coins.</span>
            </div>
          </div>
        </div>
      </section>
      {renderFeedbackDialog()}

      {pendingEarlyBirdPackage && (
        <section className="pm-active pm-early-bird" aria-label="Pending early bird package">
          <div className="pm-active-head">
            <span className="pm-active-title">Pending early bird package</span>
            <span className={`pm-status-badge ${isEarlyBirdPaid(pendingEarlyBirdPackage) ? 'pm-status-ok' : 'pm-status-bad'}`}>
              {isEarlyBirdPaid(pendingEarlyBirdPackage) ? 'Paid' : 'Payment pending'}
            </span>
          </div>

          <div className="pm-active-body">
            <div className="pm-active-coins">
              <span className="pm-active-coins-value">Ȼ {formatCoins(pendingEarlyBirdPackage.availableCoins)}</span>
              <span className="pm-active-coins-label">purchased coins</span>
            </div>

            <div className="pm-active-facets">
              <div className="pm-active-facet">
                <span className="pm-active-facet-label">Package</span>
                <span className="pm-active-facet-value">{pendingEarlyBirdPackage.packageName}</span>
                <span className="pm-active-facet-sub">{earlyBirdActivationHint(pendingEarlyBirdPackage)}</span>
              </div>

              <div className="pm-active-facet">
                <span className="pm-active-facet-label">Activation window</span>
                <span className="pm-active-facet-value">{formatEarlyBirdWindow(pendingEarlyBirdPackage)}</span>
                {user.latestTrainingPackage && (
                  <span className="pm-active-facet-sub">
                    Activating transfers remaining coins from {user.latestTrainingPackage.packageName}.
                  </span>
                )}
                <div className="pm-active-facet-action">
                  <button
                    className="pm-primary-button pm-inline-action"
                    disabled={!pendingEarlyBirdPackage.canActivateEarlyBird || busyAction === 'activate-early-bird'}
                    onClick={requestEarlyBirdActivation}
                  >
                    {busyAction === 'activate-early-bird' ? 'Activating...' : `Activate ${pendingEarlyBirdPackage.packageName}`}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      <section className="pm-controls">
        {renderSavedSelection()}
        {renderPackageTabs()}
        {showYouthToggle && activeTab !== 'private' && (
          <label className="pm-youth-toggle">
            <span>Show youth packages</span>
            <input
              type="checkbox"
              role="switch"
              checked={showYouthPackages}
              onChange={event => {
                setShowYouthPackages(event.target.checked);
                setShowAll(false);
              }}
            />
            <span className="pm-youth-toggle-track" aria-hidden="true">
              <span className="pm-youth-toggle-thumb" />
            </span>
          </label>
        )}
      </section>

      {loading ? (
        <div className="pm-empty">Loading packages...</div>
      ) : availableItems.length === 0 ? (
        <div className="pm-empty">No packages available right now.</div>
      ) : (
        <>
          {isMembershipTab || activeTab === 'private' ? (
            <section className="pm-grid">
              {availableItems.map(item => renderPackageCard(item, !!item.is_membership))}
            </section>
          ) : trainingPackageDisplay.showRecommendedSection && (
            <section className="pm-recs">
              <div className="pm-recs-head">
                <span className="pm-hero-label">
                  <FontAwesomeIcon icon={faStar} />
                  Recommended for you
                </span>
              </div>
              <div className="pm-recs-grid">
                {trainingPackageDisplay.items.map(item => renderPackageCard(item, false, { recommended: true }))}
              </div>
            </section>
          )}

          {activeTab === 'training' && !showAll && hasMoreTrainingPackages && (
            <div className="pm-showall">
              <button
                className="pm-showall-toggle"
                type="button"
                onClick={() => setShowAll(true)}
                aria-expanded={false}
              >
                {`Show all ${availableItems.length} packages`}
                <FontAwesomeIcon icon={faChevronDown} />
              </button>
            </div>
          )}

          {activeTab === 'training' && showAll && (
            <>
              {renderShowRecommendedOnlyButton()}
              <section className="pm-grid">
                {trainingPackageDisplay.items.map(item => renderPackageCard(item, false))}
              </section>
              {renderShowRecommendedOnlyButton()}
            </>
          )}
        </>
      )}

    </main>
  );
});

const formatCoins = (value?: number) => {
  return (value || 0).toLocaleString('en-US');
};

export const formatValidityMonths = (days?: number | null) => {
  const months = Math.round((days ?? 0) / 15) / 2;
  return `${months} ${months === 1 ? 'month' : 'months'}`;
};

export const formatPrice = (price: string) => {
  const numericPrice = Number(price);
  if (!Number.isFinite(numericPrice)) {
    return `${price}€`;
  }
  return `${Number.isInteger(numericPrice) ? numericPrice.toFixed(0) : numericPrice.toFixed(2)}€`;
};

export const formatDate = (date: string) => {
  return moment.parseZone(date).format('D MMM YYYY');
};

export const formatMembershipValidityDate = (date: string) => {
  return moment.parseZone(date).format('D MMM YYYY');
};

const formatDialogDate = (date: string) => {
  return moment.parseZone(date).format('MMMM D, YYYY');
};

const isEarlyBirdPaid = (pkg: TrainingPackage) => {
  return pkg.packageStatus === 'status_paid' || pkg.packageStatus === 'status_closed';
};

const formatEarlyBirdWindow = (pkg: TrainingPackage) => {
  if (!pkg.activateFromDate || !pkg.activateUntilDate) {
    return 'Not set';
  }
  const from = moment(pkg.activateFromDate).format('D MMM YYYY');
  const until = moment(pkg.activateUntilDate).format('D MMM YYYY');
  return `${from} - ${until}`;
};

const earlyBirdActivationHint = (pkg: TrainingPackage) => {
  if (!isEarlyBirdPaid(pkg)) {
    return 'Payment must be completed before activation.';
  }

  if (!pkg.activateFromDate || !pkg.activateUntilDate) {
    return 'Activation dates are missing. Please contact admin.';
  }

  const today = moment().startOf('day');
  const from = moment(pkg.activateFromDate).startOf('day');
  const until = moment(pkg.activateUntilDate).startOf('day');

  if (today.isBefore(from)) {
    return `Activation is available from ${from.format('D MMM YYYY')}.`;
  }

  if (today.isAfter(until)) {
    return `Activation window ended on ${until.format('D MMM YYYY')}.`;
  }

  return `Activation is available until ${until.format('D MMM YYYY')}.`;
};

// Map a membership subscription status to a badge tone: healthy (active/trialing),
// problem (payment issues), or neutral (ended/paused/unknown).
const membershipTone = (status?: string): 'ok' | 'bad' | 'neutral' => {
  const value = (status || '').toLowerCase();
  if (value === 'active' || value === 'trialing') {
    return 'ok';
  }
  if (/past_due|unpaid|incomplete/.test(value)) {
    return 'bad';
  }
  return 'neutral';
};

const isActiveMembershipStatus = (status?: string): boolean => {
  const value = (status || '').toLowerCase();
  return ['active', 'trialing', 'past_due', 'unpaid', 'incomplete', 'paused'].includes(value);
};

// "past_due" -> "Past due"; falls back to a friendly default when unset.
const prettyStatus = (status?: string): string => {
  if (!status) {
    return 'Active';
  }
  const label = status.replace(/_/g, ' ');
  return label.charAt(0).toUpperCase() + label.slice(1);
};

const membershipStatusText = (status: PackagesStatusResponse | null) => {
  const membership = status?.membership;
  if (!membership) {
    return 'No monthly top-up';
  }
  if (isCancellationScheduled(status)) {
    return `Cancellation scheduled for ${membership.pending_effective_at ? formatDate(membership.pending_effective_at) : 'next cycle'}`;
  }
  if (membership.pending_action) {
    return `${membership.pending_action} on ${membership.pending_effective_at ? formatDate(membership.pending_effective_at) : 'next cycle'}`;
  }
  if (membership.next_billing_at) {
    return `Next billing ${formatDate(membership.next_billing_at)}`;
  }
  return membership.status;
};

const isCancellationScheduled = (status: PackagesStatusResponse | null) => {
  return status?.membership?.pending_action === 'cancel';
};

const isTierChangeScheduled = (status: PackagesStatusResponse | null) => {
  return status?.membership?.pending_action === 'upgrade' || status?.membership?.pending_action === 'downgrade';
};

const pendingTierLabel = (membership: NonNullable<PackagesStatusResponse['membership']>): string => {
  const action = membership.pending_action === 'downgrade' ? 'Downgrade' : 'Upgrade';
  const effectiveDate = membership.pending_effective_at || membership.next_billing_at;
  return effectiveDate ? `${action} on ${formatDate(effectiveDate)}` : `${action} next cycle`;
};

const newRequestId = () => {
  if (window.crypto && 'randomUUID' in window.crypto) {
    return window.crypto.randomUUID();
  }
  return `pm-${Date.now()}-${Math.random().toString(16).slice(2)}`;
};

const cardErrorText = (error: any) => {
  if ([401, 403].includes(Number(error?.status)) || rawErrorCode(error).toUpperCase().includes('UNAUTHORIZED')) {
    return 'Your session has expired. Please sign in again.';
  }
  return 'We could not update your membership card. Please try changing your card again.';
};

const errorText = (error: any) => {
  const status = Number(error?.status);
  const raw = rawErrorCode(error).toUpperCase();

  if (status === 401 || status === 403 || raw.includes('UNAUTHORIZED')) {
    return 'Your session has expired. Please sign in again.';
  }
  if (status === 404) {
    return 'This package or membership option is no longer available. Please refresh and choose another option.';
  }
  if (status === 409) {
    return 'This change could not be completed because your membership was updated elsewhere. Please refresh and try again.';
  }
  if (raw.includes('CANNOT BE CANCELED YET')) {
    return 'Your membership cannot be canceled yet. Please check the available cancellation date.';
  }
  if (raw.includes('MEMBERSHIP IS NOT ACTIVE')) {
    return 'This membership is no longer active. Please refresh the page.';
  }
  if (raw.includes('PAYMENT') || raw.includes('CARD')) {
    return 'The payment could not be completed. Please check your payment method and try again.';
  }
  if (status >= 500 || raw.includes('SERVICE TEMPORARILY UNAVAILABLE')) {
    return 'The service is temporarily unavailable. Please try again shortly.';
  }
  return 'We could not complete this action. Please try again.';
};

const rawErrorCode = (error: any): string => {
  if (!error) return '';
  if (typeof error === 'string') return error;
  if (typeof error.message === 'string') return error.message;
  if (typeof error.error === 'string') return error.error;
  return '';
};

export default PackagesMembership;
